import Firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import ReactGA from 'react-ga4';

import ORDER_STATUS from './helpers/STATUS';
import { cancelPaymentIntent, capturePaymentIntent } from '../stripe';
import cancelDiscountUsage from '../discounts/cancelDiscountUsage';

const LOCK_TIMEOUT_MS = 120000;
const activeStatuses = [
  ORDER_STATUS.inValidation,
  ORDER_STATUS.inProgress,
  ORDER_STATUS.ready,
  ORDER_STATUS.inDelivery,
];

const finishOrder = async (order, kind, by) => {
  if (!Firebase.auth().currentUser) {
    throw new Error('Vous devez être connecté pour modifier une commande.');
  }
  if (
    !order?.id ||
    ![ORDER_STATUS.canceled, ORDER_STATUS.delivered].includes(kind)
  ) {
    throw new Error('Finalisation de commande invalide.');
  }

  const db = Firebase.firestore();
  const ref = db.collection('orders').doc(order.id);
  // Generated once, outside callbacks that Firestore may retry; no document is created.
  const owner = db.collection('orders').doc().id;
  const current = await db.runTransaction(async transaction => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists) throw new Error("Cette commande n'existe pas.");
    const data = snapshot.data();
    if (
      !activeStatuses.includes(data.status) ||
      (kind === ORDER_STATUS.delivered &&
        data.status !== ORDER_STATUS.inDelivery)
    ) {
      throw new Error(
        'Le statut de cette commande ne permet plus cette finalisation.'
      );
    }
    if (
      typeof data.paymentIntentId !== 'string' ||
      !data.paymentIntentId.trim()
    ) {
      throw new Error('Paiement de la commande introuvable.');
    }
    if (data.inTreatment) {
      if (
        data.inTreatmentAction !== kind ||
        !data.inTreatmentOwner ||
        !(data.updatedAt instanceof Firebase.firestore.Timestamp)
      ) {
        throw new Error(
          'Traitement précédent non vérifiable. Contactez le support pour vérifier le paiement.'
        );
      }
      if (Date.now() - data.updatedAt.toMillis() <= LOCK_TIMEOUT_MS) {
        throw new Error(
          'Commande en cours de traitement, réessayez après deux minutes.'
        );
      }
    }

    transaction.update(ref, {
      inTreatment: true,
      inTreatmentAction: kind,
      inTreatmentOwner: owner,
      updatedAt: Firebase.firestore.FieldValue.serverTimestamp(),
    });
    return data;
  });

  ReactGA.event({
    category: 'Order',
    action:
      kind === ORDER_STATUS.canceled
        ? `Canceled order by ${by || 'shop'}`
        : 'Finished order',
  });
  const payment =
    kind === ORDER_STATUS.canceled
      ? await cancelPaymentIntent({ paymentIntentID: current.paymentIntentId })
      : await capturePaymentIntent({ orderID: order.id });
  const expectedPaymentStatus =
    kind === ORDER_STATUS.canceled ? 'canceled' : 'succeeded';
  if (payment?.payment_intent_status !== expectedPaymentStatus) {
    // A timeout/error does not prove that Stripe did not act. Keep the typed lock.
    throw new Error(
      'Résultat du paiement non confirmé. Réessayez la même action après deux minutes ou contactez le support.'
    );
  }

  await db.runTransaction(async transaction => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists) throw new Error("Cette commande n'existe plus.");
    const data = snapshot.data();
    if (
      !data.inTreatment ||
      data.inTreatmentOwner !== owner ||
      data.inTreatmentAction !== kind ||
      data.status !== current.status
    ) {
      throw new Error(
        'Le traitement de cette commande a changé. Actualisez la commande avant de réessayer.'
      );
    }
    transaction.update(ref, {
      status: kind,
      ...(kind === ORDER_STATUS.canceled
        ? { canceledAt: Date.now(), canceledBy: by || 'shop' }
        : { deliverAt: Date.now() }),
      inTreatment: false,
      inTreatmentAction: Firebase.firestore.FieldValue.delete(),
      inTreatmentOwner: Firebase.firestore.FieldValue.delete(),
      updatedAt: Firebase.firestore.FieldValue.serverTimestamp(),
    });
  });

  const result = { success: true, status: kind };
  if (kind === ORDER_STATUS.canceled && current.discount?.code) {
    try {
      await cancelDiscountUsage({
        code: current.discount.code,
        userID: current.clientID,
      });
    } catch (error) {
      console.error('Commande annulée, remise non rétablie', error);
      result.warning =
        'Commande annulée. Le rétablissement de la remise a échoué ; contactez le support.';
    }
  }
  return result;
};

export default finishOrder;
